import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { User, LogOut, Trophy, ChevronDown, Edit2, X, Sparkles, BarChart3 } from 'lucide-react';

const UserMenu = ({ user, onLogout, onViewLeaderboard, onNicknameChange, onViewStats }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showNicknameModal, setShowNicknameModal] = useState(false);
  const [newNickname, setNewNickname] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  if (!user) return null;

  const handleNicknameSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    const trimmedNickname = newNickname.trim();
    if (trimmedNickname.length < 2 || trimmedNickname.length > 20) {
      setError('Nickname must be 2-20 characters');
      return;
    }

    setIsLoading(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const sessionToken = localStorage.getItem('session_token');
      const headers = {
        'Content-Type': 'application/json',
      };
      if (sessionToken) {
        headers['Authorization'] = `Bearer ${sessionToken}`;
      }
      
      const response = await fetch(`${backendUrl}/api/auth/nickname`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ nickname: trimmedNickname }),
        credentials: 'include',
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to update nickname');
      }

      const updatedUser = await response.json();
      if (onNicknameChange) {
        onNicknameChange(updatedUser);
      }
      setShowNicknameModal(false);
      setNewNickname('');
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const openNicknameModal = () => {
    setNewNickname(user.nickname || '');
    setError('');
    setIsOpen(false);
    setShowNicknameModal(true);
  };

  return (
    <>
      <div className="relative">
        <Button
          onClick={() => setIsOpen(!isOpen)}
          variant="outline"
          className="bg-white/10 border-white/20 text-white hover:bg-white/20 flex items-center gap-2"
          data-testid="user-menu-button"
        >
          {user.picture ? (
            <img src={user.picture} alt="" className="w-6 h-6 rounded-full" />
          ) : (
            <User className="w-5 h-5" />
          )}
          <span className="hidden sm:inline">{user.nickname || user.name}</span>
          <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </Button>

        {isOpen && (
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 z-40"
              onClick={() => setIsOpen(false)}
            />
            
            {/* Dropdown */}
            <div className="absolute right-0 mt-2 w-48 bg-slate-800 border border-white/20 rounded-lg shadow-xl z-50 overflow-hidden">
              <div className="p-3 border-b border-white/10">
                <p className="text-white font-medium truncate">{user.nickname || user.name}</p>
                <p className="text-gray-400 text-sm truncate">{user.email}</p>
              </div>
              
              <div className="p-1">
                <button
                  onClick={openNicknameModal}
                  className="w-full flex items-center gap-2 px-3 py-2 text-gray-300 hover:bg-white/10 rounded transition-colors"
                  data-testid="change-nickname-button"
                >
                  <Edit2 className="w-4 h-4" />
                  Change Nickname
                </button>
                
                <button
                  onClick={() => {
                    setIsOpen(false);
                    onViewStats();
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-gray-300 hover:bg-white/10 rounded transition-colors"
                  data-testid="view-stats-button"
                >
                  <BarChart3 className="w-4 h-4" />
                  My Stats
                </button>
                
                <button
                  onClick={() => {
                    setIsOpen(false);
                    onViewLeaderboard();
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-gray-300 hover:bg-white/10 rounded transition-colors"
                  data-testid="view-leaderboard-button"
                >
                  <Trophy className="w-4 h-4" />
                  Leaderboard
                </button>
                
                <button
                  onClick={() => {
                    setIsOpen(false);
                    onLogout();
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-red-400 hover:bg-white/10 rounded transition-colors"
                  data-testid="sign-out-button"
                >
                  <LogOut className="w-4 h-4" />
                  Sign Out
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Nickname Change Modal */}
      {showNicknameModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={() => setShowNicknameModal(false)}
          />
          
          {/* Modal */}
          <div className="relative bg-slate-800 border border-white/20 rounded-2xl p-6 max-w-md w-full shadow-2xl">
            <button
              onClick={() => setShowNicknameModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
              data-testid="close-nickname-modal"
            >
              <X className="w-5 h-5" />
            </button>
            
            <div className="text-center mb-6">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                {user.picture ? (
                  <img src={user.picture} alt="" className="w-full h-full rounded-full" />
                ) : (
                  <User className="w-8 h-8 text-white" />
                )}
              </div>
              <h2 className="text-xl font-bold text-white">Change Nickname</h2>
              <p className="text-gray-400 text-sm mt-1">This will update your leaderboard name</p>
            </div>

            <form onSubmit={handleNicknameSubmit} className="space-y-4">
              <div className="relative">
                <Input
                  type="text"
                  value={newNickname}
                  onChange={(e) => setNewNickname(e.target.value)}
                  placeholder="Enter new nickname"
                  className="bg-white/10 border-white/20 text-white placeholder:text-gray-400 text-center text-lg py-5"
                  maxLength={20}
                  autoFocus
                  data-testid="nickname-input"
                />
                <Sparkles className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-yellow-400" />
              </div>
              
              {error && (
                <p className="text-red-400 text-sm text-center">{error}</p>
              )}
              
              <p className="text-gray-400 text-sm text-center">
                {newNickname.length}/20 characters
              </p>

              <div className="flex gap-3">
                <Button
                  type="button"
                  onClick={() => setShowNicknameModal(false)}
                  variant="outline"
                  className="flex-1 bg-white/10 border-white/20 text-white hover:bg-white/20"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isLoading || newNickname.trim().length < 2}
                  className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white"
                  data-testid="save-nickname-button"
                >
                  {isLoading ? 'Saving...' : 'Save'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default UserMenu;
